import { useCallback, useMemo } from 'react';
import { Tag, useUserInputsApiFacade } from '../../../../../apis/facades/useUserInputsApiFacade';
import { useDispatch, useSelector } from 'react-redux';
import {
  addTag,
  updateTag,
  deleteTag,
  setTagConfigurations,
  clearTagConfigurations,
  deleteTagFromConfigurationUserInputs,
} from '../../../../../reducers/documentation/userInputsReducer';
import { telemetry } from '../../../../../telemetry';
import { useSweepNotifications } from '../../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../../reducers/notificationsReducer';
import { TagType } from '../../header/TagsHeader';
import { UserInputTypes } from '@server/user-inputs';
import { selectAccountUsers } from '../../../../../reducers/accountUsersReducer';
import { useSalesforceCard } from '../../hooks/useSalesforceCard';
import { selectEnvironments } from '../../../../../reducers/global/globalReducer';
import {
  selectLayoutsByObjectName,
  selectParsedFields,
  selectParsedRecordTypes,
} from '../../../../../reducers/global/parserReducer';
import { selectCrmOrgRollups } from '../../../../../reducers/global/rollupReducers';
import { getOptimizedConfigurationItem } from '../../hooks/useGetConfigurationItem';
import { parseIdToValueAndNameToLabel } from './utils';
import groupBy from 'lodash/groupBy';
import { ConfigurationItemMap } from '../../../configuration-item-tables/shared/helpers';
import { useGetFilteredConfigurationItems } from '../../../selected-object/filters/useGetFilteredConfigurationItems';
import { InlineAutocompleteItem } from '../../../../common/InlineAutocomplete/InlineAutocomplete';
import { selectTagsList } from '../../../../../reducers/documentation/userInputsReducer';
import { UnclassifiedTypes, DocumentationCardContentViewType } from '../../types';
import { SalesforceItem } from '../../../../parser/ParserTypes';

export const useTags = (crmOrgId: string) => {
  const dispatch = useDispatch();
  const { handleSetConfigurationItem } = useSalesforceCard(crmOrgId);
  const {
    post_tag,
    patch_tag,
    delete_tag,
    get_tagConfigurations,
    delete_userConfigurations,
    get_userConfigurations,
  } = useUserInputsApiFacade();
  const { addNotification } = useSweepNotifications();

  const objectLayouts = useSelector(selectLayoutsByObjectName(crmOrgId));
  const parsedFields = useSelector(selectParsedFields(crmOrgId));
  const rollups = useSelector(selectCrmOrgRollups(crmOrgId));
  const globalEnvironments = useSelector(selectEnvironments);
  const tags = useSelector(selectTagsList);
  const users = useSelector(selectAccountUsers);
  const _parsedUsers = parseIdToValueAndNameToLabel(users ?? []);
  const parsedRecordTypes = useSelector(selectParsedRecordTypes(crmOrgId));
  const configurationItems = useGetFilteredConfigurationItems(crmOrgId);

  const { automations, alerts, matching, dedup, assignments, scheduledAssignments, sweepFields } =
    globalEnvironments?.[crmOrgId]?.data ?? {};

  const indexedLookups = useMemo(() => {
    const tagsById = new Map(tags?.map((tag) => [tag.id, tag]) ?? []);
    const usersById = new Map(_parsedUsers?.map((user) => [user.value, user]) ?? []);
    const layoutsById = new Map<string, any>();
    const layoutsByName = new Map<string, any>();
    const allLayoutsArray = Object.values(objectLayouts ?? {})
      .map((layouts) => layouts.layouts)
      .flat();

    allLayoutsArray?.forEach((layout) => {
      if (layout.id) layoutsById.set(layout.id, layout);
      if (layout.name) layoutsByName.set(layout.name, layout);
    });

    const rulesById = new Map<string, any>();
    const rulesByName = new Map<string, any[]>();
    configurationItems?.forEach((rule) => {
      if (rule.id) rulesById.set(rule.id, rule);
      if (rule.name) {
        const existingRules = rulesByName.get(rule.name) ?? [];
        rulesByName.set(rule.name, [...existingRules, rule]);
      }
    });

    const rollupsById = new Map<string, any>();
    const rollupsByFieldId = new Map<string, any>();
    rollups?.forEach((rollup) => {
      if (rollup.rollupId) rollupsById.set(rollup.rollupId, rollup);
      if (rollup.rollupField?.id) rollupsByFieldId.set(rollup.rollupField.id, rollup);
    });

    const recordTypesByName = new Map<string, any>();
    const recordTypesByKey = new Map<string, any>();
    parsedRecordTypes?.forEach((rt) => {
      if (rt.name) recordTypesByName.set(`${rt.name}-${rt.objectApiNames[0]}`, rt);
    });

    const sweepFieldsById = new Map(Object.entries(sweepFields ?? {}));

    return {
      tagsById,
      usersById,
      layoutsById,
      layoutsByName,
      rulesById,
      rulesByName,
      rollupsById,
      rollupsByFieldId,
      recordTypesByName,
      recordTypesByKey,
      sweepFieldsById,
      allLayoutsArray,
    };
  }, [
    tags,
    _parsedUsers,
    objectLayouts,
    configurationItems,
    rollups,
    parsedRecordTypes,
    sweepFields,
  ]);

  const cachedExpensiveOperations = useMemo(() => {
    const makeNotUndefined = (items?: AutomationStructureNew[]) => items ?? [];

    const mergedAutomations = groupBy(
      [
        ...makeNotUndefined(automations),
        ...makeNotUndefined(alerts),
        ...makeNotUndefined(assignments),
        ...makeNotUndefined(matching),
        ...makeNotUndefined(dedup),
        ...makeNotUndefined(scheduledAssignments),
      ].flat(),
      'automationId',
    );

    const allFieldsFlattened: Array<any> = [];
    const fieldsById = new Map<string, any>();

    if (parsedFields) {
      Object.keys(parsedFields).forEach((objectName) => {
        parsedFields[objectName]?.forEach((field) => {
          const fieldWithObject = { ...field, objectName };
          allFieldsFlattened.push(fieldWithObject);
          if (field.id) fieldsById.set(field.id, fieldWithObject);
        });
      });
    }

    const elementsById = new Map();
    const elementsByName = new Map();
    const metadataElementsById = new Map();

    return {
      mergedAutomations,
      allFieldsFlattened,
      fieldsById,
      elementsById,
      elementsByName,
      metadataElementsById,
    };
  }, [automations, alerts, assignments, matching, dedup, scheduledAssignments, parsedFields]);

  const clearTagOrUserConfigurations = useCallback(() => {
    dispatch(clearTagConfigurations());
  }, [dispatch]);

  const _getConfigurationItem = useCallback(
    (configurationId: string) => {
      return getOptimizedConfigurationItem({
        searchCriteria: {
          id: configurationId,
          searchByName: false,
        },
        parsedFields,
        users,
        indexedLookups,
        cachedExpensiveOperations,
      });
    },
    [parsedFields, users, indexedLookups, cachedExpensiveOperations],
  );

  const createConfigurationItemMap = useCallback(
    (response: string[]) => {
      return groupBy(
        response.map((itemId) => _getConfigurationItem(itemId)).filter((item) => !!item),
        'parentType',
      ) as ConfigurationItemMap;
    },
    [_getConfigurationItem],
  );

  const fetchTagConfigurations = useCallback(
    async ({ metadataId, crmOrgId }: { metadataId: string; crmOrgId: string }) => {
      try {
        const response = await get_tagConfigurations(metadataId, crmOrgId);
        dispatch(
          setTagConfigurations({
            configurations: {
              [UserInputTypes.Tags]: createConfigurationItemMap(response),
              [UserInputTypes.Owner]: createConfigurationItemMap([]),
              [UserInputTypes.Collaborators]: createConfigurationItemMap([]),
            },
            tagId: metadataId,
          }),
        );
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [get_tagConfigurations, dispatch, createConfigurationItemMap],
  );

  const fetchUserConfigurations = useCallback(
    async ({ metadataId, crmOrgId }: { metadataId: string; crmOrgId: string }) => {
      try {
        const response = await get_userConfigurations(metadataId, crmOrgId);
        dispatch(
          setTagConfigurations({
            configurations: {
              [UserInputTypes.Tags]: createConfigurationItemMap([]),
              [UserInputTypes.Owner]: createConfigurationItemMap(response[UserInputTypes.Owner]),
              [UserInputTypes.Collaborators]: createConfigurationItemMap(
                response[UserInputTypes.Collaborators],
              ),
            },
            tagId: metadataId,
          }),
        );
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [get_userConfigurations, dispatch, createConfigurationItemMap],
  );

  const onAddTag = useCallback(
    async (tag: Omit<Tag, 'id'>) => {
      try {
        const tagId = await post_tag(tag);
        dispatch(addTag({ tag: { ...tag, id: tagId } }));
        return tagId;
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [post_tag, dispatch],
  );

  const onUpdateTag = useCallback(
    async (tag: Tag) => {
      try {
        dispatch(updateTag({ tag }));
        await patch_tag(tag);
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [patch_tag, dispatch],
  );

  const onDeleteTag = useCallback(
    async (tagId: string, tagType: TagType) => {
      try {
        if (tagType === TagType.tag) {
          await delete_tag(tagId);
        } else {
          await delete_userConfigurations(tagId);
        }
        dispatch(deleteTag({ tagId }));
        dispatch(deleteTagFromConfigurationUserInputs({ tagId, crmOrgId }));

        const message =
          tagType === TagType.tag ? `Tag successfully removed` : 'User successfully unlinked';

        addNotification({
          message,
          variant: SweepNotificationVariant.Success,
        });
        return true;
      } catch (error) {
        telemetry.captureError(error);
        addNotification({
          message: `Failed to remove due to technical issues`,
          keepOpen: true,
          variant: SweepNotificationVariant.Error,
        });
        return false;
      }
    },
    [delete_tag, crmOrgId, delete_userConfigurations, addNotification, dispatch],
  );

  const onTagClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      tag: InlineAutocompleteItem,
      clearHistory?: boolean,
    ) => {
      const tagItem: SalesforceItem = {
        id: tag.value,
        metadataId: tag.value,
        name: tag.label,
        label: tag.label,
        parentType: UnclassifiedTypes.tag,
        objectApiNames: [],
        type: 'tag',
      };

      handleSetConfigurationItem({
        item: tagItem,
        contentType: DocumentationCardContentViewType.editTag,
        clearHistory,
      });
    },
    [handleSetConfigurationItem],
  );

  return {
    onAddTag,
    onDeleteTag,
    onUpdateTag,
    fetchTagConfigurations,
    fetchUserConfigurations,
    clearTagOrUserConfigurations,
    onTagClick,
  };
};
